import { useState } from 'react';
import { AlertCircle, Download, FlaskConical } from 'lucide-react';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { ImportFileDropzone, MINE_TYPES } from '@features/files/ImportFileDropZone';
import { EmissionScope, Scope1ActivityType, BULK_IMPORT_MAX_ROWS } from '../../types/emissions';
import { ImportGuidelines } from './ImportGuidelines';
import { SeedTemplateModal, type SeedTemplateParams } from './SeedTemplateModal';
import { type FormData } from './types';

interface BulkUploadStepProps {
  initiativeId: string;
  formData: Pick<FormData, 'scope' | 'activityType'>;
  importFile: File | undefined;
  parseError: string | null;
  isDownloadingTemplate: boolean;
  onDownloadTemplate: () => void;
  onFilesAdded: (files: File[]) => void;
  onFileRemove: () => void;
  onBack: () => void;
  isStaff?: boolean;
  onDownloadSeedTemplate?: (params: SeedTemplateParams) => void;
  isDownloadingSeedTemplate?: boolean;
}

export const BulkUploadStep = ({
  initiativeId,
  formData,
  importFile,
  parseError,
  isDownloadingTemplate,
  onDownloadTemplate,
  onFilesAdded,
  onFileRemove,
  isStaff,
  onDownloadSeedTemplate,
  isDownloadingSeedTemplate,
}: BulkUploadStepProps) => {
  const [seedModalOpen, setSeedModalOpen] = useState(false);

  return (
    <>
      <Card className='tw:mb-4'>
        <CardContent className='tw:p-4'>
          <div className='tw:flex tw:justify-between tw:items-center'>
            <div>
              <h5 className='tw:font-semibold tw:mb-1'>Step 1: Download Template</h5>
              <p className='tw:text-sm tw:text-muted-foreground tw:mb-0'>
                Download the XLSX template and fill it in with your activity data
              </p>
            </div>
            <div className='tw:flex tw:gap-2'>
              <Button variant='outline' onClick={onDownloadTemplate} disabled={isDownloadingTemplate}>
                <Download className='tw:size-4' />
                {isDownloadingTemplate ? 'Downloading...' : 'Download Template'}
              </Button>
              {isStaff && onDownloadSeedTemplate && (
                <Button variant='outline' onClick={() => setSeedModalOpen(true)} disabled={isDownloadingSeedTemplate}>
                  <FlaskConical className='tw:size-4' />
                  {isDownloadingSeedTemplate ? 'Generating...' : 'Seed Template'}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className='tw:p-4'>
          <div className='tw:mb-4'>
            <h5 className='tw:font-semibold tw:mb-1'>Step 2: Upload Your File</h5>
            <p className='tw:text-sm tw:text-muted-foreground tw:mb-0'>
              Fill in the template and upload it here
            </p>
          </div>
          <ImportFileDropzone
            importFile={importFile}
            onFilesAdded={onFilesAdded}
            onFileRemove={onFileRemove}
            accept={{ ...MINE_TYPES.CSV, ...MINE_TYPES.EXCEL }}
            dropText='Drag and drop your CSV or Excel file here'
            hint={`Supported formats: CSV, XLSX, XLS — maximum ${BULK_IMPORT_MAX_ROWS} rows per import`}
          />
          {parseError && (
            <div className='tw:flex tw:gap-2 tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:p-3 tw:text-red-800 tw:text-sm tw:mt-3'>
              <AlertCircle className='tw:size-4 tw:shrink-0 tw:mt-0.5' />
              {parseError}
            </div>
          )}
        </CardContent>
      </Card>

      <ImportGuidelines
        initiativeId={initiativeId}
        scope={formData.scope as EmissionScope}
        activityType={formData.scope === EmissionScope.Scope1 ? formData.activityType as Scope1ActivityType : undefined}
      />

      {isStaff && onDownloadSeedTemplate && (
        <SeedTemplateModal
          isOpen={seedModalOpen}
          toggle={() => setSeedModalOpen(false)}
          onDownload={(params) => {
            onDownloadSeedTemplate(params);
            setSeedModalOpen(false);
          }}
          isDownloading={isDownloadingSeedTemplate ?? false}
        />
      )}
    </>
  );
};
